/**
 * The Inbox: open alerts. One card per rule, with every ship it is firing on
 * inside it (2026-09-27, `docs/design/sketches/argus-inbox-grouped.html`) -
 * a rule on eleven ships is one alert, not eleven rows. What is over lives in
 * History (`argus-history-room.ts`).
 *
 * The card is `argus-alerts.ts`. With nothing open there is no colour on the
 * page at all (rule 10). The words are the plan's: a suspicion to look at,
 * never a fault (rule 5).
 *
 * Built for many (2026-09-28, after fifty demo alerts ran at a few frames a
 * second): filters by severity, by "not yet acknowledged" and by text; pages
 * of `PAGE_SIZE`; and a card scrolled out of view stops animating.
 */

import { html, nothing, type PropertyValues, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { canDo } from '../../state/store.ts';
import { setArgusRoom, openArgusEpisode } from './argus-lens.ts';
import { groupAlerts, renderAlertCard, type ArgusAlert } from './argus-alerts.ts';
import {
  inbox,
  inboxError,
  inboxRuleFilter,
  inboxStatus,
  loadInbox,
  markEpisodes,
  rulesRoomOpenRule,
} from './argus-inbox-store.ts';
import { loadWatchers } from './argus-rules-store.ts';
import { paginate, renderPager } from './argus-pager.ts';

/** Six rows of four on a wide screen. */
const PAGE_SIZE = 24;

type Severity = 'critical' | 'watch' | 'info';
const SEVERITIES: Severity[] = ['critical', 'watch', 'info'];

@customElement('argus-inbox-room')
export class ArgusInboxRoom extends LightElement {
  private store = new StoreController(this, [inbox, inboxStatus, inboxError, inboxRuleFilter]);
  private refreshTimer: number | undefined;

  @state() private menu: { alert: ArgusAlert; x: number; y: number } | null = null;
  @state() private error: string | null = null;
  @state() private busy = false;
  @state() private page = 0;
  @state() private query = '';
  @state() private severity: Severity | null = null;
  @state() private onlyNew = false;

  /** Pauses the glow of cards scrolled out of view - they are the expensive part. */
  private visibility: IntersectionObserver | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    void loadInbox();
    void loadWatchers();
    this.refreshTimer = window.setInterval(() => {
      void loadInbox();
      void loadWatchers();
    }, 60_000);
    document.addEventListener('pointerdown', this.onOutside, true);
    document.addEventListener('keydown', this.onKey);
    window.addEventListener('scroll', this.closeMenu, true);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.visibility?.disconnect();
    this.visibility = null;
    window.clearInterval(this.refreshTimer);
    document.removeEventListener('pointerdown', this.onOutside, true);
    document.removeEventListener('keydown', this.onKey);
    window.removeEventListener('scroll', this.closeMenu, true);
  }

  override updated(changed: PropertyValues): void {
    super.updated(changed);
    this.visibility ??= new IntersectionObserver(
      (entries) => {
        for (const entry of entries) entry.target.classList.toggle('glow-paused', !entry.isIntersecting);
      },
      // A little ahead of the viewport, so a card is already moving as it arrives.
      { rootMargin: '200px 0px' },
    );
    // Observing again is a no-op; new slots from a page change are picked up.
    for (const slot of this.querySelectorAll('.argus-ac-slot')) this.visibility.observe(slot);
  }

  /** Change a filter and go back to the first page, where its results start. */
  private refilter(change: () => void): void {
    change();
    this.page = 0;
  }

  private keeps(alert: ArgusAlert): boolean {
    if (this.severity && alert.severity !== this.severity) return false;
    if (this.onlyNew && !alert.pending.length) return false;
    const q = this.query.trim().toLowerCase();
    if (!q) return true;
    return [alert.ruleName, alert.sentence ?? '', ...alert.items.map((i) => i.entity_name)]
      .join(' ')
      .toLowerCase()
      .includes(q);
  }

  private renderFilters(alerts: ArgusAlert[]): TemplateResult | typeof nothing {
    // A handful of alerts needs no tools to sort through.
    if (alerts.length <= 8 && !this.query && !this.severity && !this.onlyNew) return nothing;
    const count = (sev: Severity): number => alerts.filter((a) => a.severity === sev).length;
    const chip = (label: string, on: boolean, toggle: () => void, n?: number): TemplateResult =>
      html`<button type="button" class="argus-fchip ${on ? 'is-on' : ''}" aria-pressed=${on ? 'true' : 'false'}
        @click=${() => this.refilter(toggle)}>${label}${n != null ? html`<b>${n}</b>` : nothing}</button>`;
    const fresh = alerts.filter((a) => a.pending.length).length;
    return html`<div class="argus-toolbar argus-ibx-tools">
      <input
        class="argus-search"
        type="search"
        placeholder="Find a rule or a ship…"
        aria-label="Find an alert"
        .value=${this.query}
        @input=${(e: Event) => this.refilter(() => (this.query = (e.target as HTMLInputElement).value))}
      />
      ${chip('All', this.severity === null, () => (this.severity = null), alerts.length)}
      ${SEVERITIES.map((sev) =>
        count(sev)
          ? chip(sev[0]!.toUpperCase() + sev.slice(1), this.severity === sev,
              () => (this.severity = this.severity === sev ? null : sev), count(sev))
          : nothing,
      )}
      <span class="argus-toolbar-gap" aria-hidden="true"></span>
      ${chip('Not yet acknowledged', this.onlyNew, () => (this.onlyNew = !this.onlyNew), fresh)}
    </div>`;
  }

  private closeMenu = (): void => {
    if (this.menu) this.menu = null;
  };

  private onOutside = (e: Event): void => {
    if (!this.menu) return;
    if ((e.target as Element | null)?.closest('.cat-menu')) return;
    this.menu = null;
  };

  private onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.closeMenu();
  };

  private filterTo(alert: ArgusAlert | null): void {
    this.menu = null;
    inboxRuleFilter.set(alert ? { id: alert.ruleId, name: alert.ruleName } : null);
    void loadInbox();
  }

  private async mark(ids: number[], mark: 'seen' | null): Promise<void> {
    this.menu = null;
    this.busy = true;
    this.error = await markEpisodes(ids, mark);
    this.busy = false;
  }

  private renderMenu(): TemplateResult | typeof nothing {
    const at = this.menu;
    if (!at) return nothing;
    const { alert } = at;
    const x = Math.min(at.x, window.innerWidth - 220);
    const y = Math.min(at.y, window.innerHeight - 180);
    const entry = (icon: string, label: string, run: () => void): TemplateResult =>
      html`<button class="cat-menu-item" role="menuitem" type="button" @click=${run}>
        <span class="cat-menu-icon" aria-hidden="true">${icon}</span> ${label}
      </button>`;
    const acked = alert.items.filter((i) => i.mark === 'seen').map((i) => i.id);
    const may = canDo('argus:triage') && alert.mayHandle;
    return html`<div class="cat-menu argus-ibx-menu" role="menu" style=${`left:${x}px; top:${y}px`}>
      ${may && alert.pending.length
        ? entry('✓', alert.pending.length > 1 ? `Acknowledge all ${alert.pending.length}` : 'Acknowledge', () =>
            void this.mark(alert.pending.map((i) => i.id), 'seen'))
        : nothing}
      ${may && acked.length ? entry('○', 'Undo acknowledge', () => void this.mark(acked, null)) : nothing}
      ${may ? html`<div class="cat-menu-sep"></div>` : nothing}
      ${entry('▸', 'Open the rule', () => {
        this.menu = null;
        rulesRoomOpenRule.set(alert.ruleId);
        setArgusRoom('rules');
      })}
      ${inboxRuleFilter.value
        ? entry('≡', 'Show every rule', () => this.filterTo(null))
        : entry('≡', 'Only this rule', () => this.filterTo(alert))}
    </div>`;
  }

  private renderBody(alerts: ArgusAlert[]): TemplateResult {
    const shown = alerts.filter((a) => this.keeps(a));
    const box = inbox.value;
    if (!box && (inboxStatus.value === 'idle' || inboxStatus.value === 'loading')) {
      return html`<p class="argus-room-waiting">Reading what the watcher found…</p>`;
    }
    if (inboxStatus.value === 'failed' && !box) {
      return html`<p class="argus-room-waiting">
        <span class="argus-room-waiting-label">Not readable</span>${inboxError.value}
      </p>`;
    }
    if (box && !box.available) {
      return html`<p class="argus-room-waiting">
        <span class="argus-room-waiting-label">No watcher tables</span>
        This database has never heard from a watcher. Nothing can be open until one reports.
      </p>`;
    }
    if (!alerts.length) {
      return html`<p class="argus-ibx-quiet">
        <strong>No open alerts.</strong>
        <span>Nothing is firing${inboxRuleFilter.value ? ' for this rule' : ''}.</span>
      </p>`;
    }
    if (!shown.length) {
      return html`${this.renderFilters(alerts)}
        <p class="argus-lempty">No open alert matches the search or filters.</p>`;
    }
    const p = paginate(shown, this.page, PAGE_SIZE);
    const go = (page: number): void => {
      this.page = page;
      // The first card, just below the pinned pager.
      void this.updateComplete.then(() => this.querySelector('.argus-ac-cards')?.scrollIntoView({ block: 'start' }));
    };
    const acknowledge = canDo('argus:triage')
      ? (alert: ArgusAlert): void => void this.mark(alert.pending.map((i) => i.id), 'seen')
      : undefined;
    // Each card sits in a slot the observer can mark: the card's own class
    // attribute is Lit's, and a re-render would drop a class added to it.
    return html`${this.renderFilters(alerts)}
      ${renderPager(p, 'alerts', go)}
      <div class="argus-ac-cards">
        ${repeat(
          p.items,
          (a) => a.key,
          (a) => html`<div class="argus-ac-slot">${renderAlertCard(a, {
            acknowledge,
            busy: this.busy,
            review: (alert) => openArgusEpisode((alert.pending[0] ?? alert.items[0])!.id),
            menu: (alert, x, y) => (this.menu = { alert, x, y }),
          })}</div>`,
        )}
      </div>`;
  }

  override render(): TemplateResult {
    const all = inbox.value?.open ?? [];
    const alerts = groupAlerts(all);
    // Ships still under review; the decided ones ride along inside their alert.
    const open = all.filter((i) => i.state === 'open');
    const pending = alerts.filter((a) => a.pending.length).length;
    const filter = inboxRuleFilter.value;
    return html`
      <div class="argus-ibx">
        <div class="argus-ibx-head">
          <h2 class="argus-ibx-title">Open alerts</h2>
          ${alerts.length
            ? html`<span class="argus-lfaint">
                ${alerts.length} alert${alerts.length === 1 ? '' : 's'} · ${open.length}
                ship${open.length === 1 ? '' : 's'}${pending ? ` · ${pending} not yet acknowledged` : ''}
              </span>`
            : nothing}
          <span class="argus-ibx-spacer" aria-hidden="true"></span>
          ${filter
            ? html`<button type="button" class="argus-fchip is-on" title="Show every rule" @click=${() => this.filterTo(null)}>
                Only: ${filter.name} <b aria-hidden="true">×</b>
              </button>`
            : nothing}
          <button type="button" class="argus-lmore" @click=${() => setArgusRoom('history')}>
            What is over is in History →
          </button>
        </div>
        ${this.error ? html`<p class="argus-form-error" role="alert">${this.error}</p>` : nothing}
        ${this.renderBody(alerts)}
      </div>
      ${this.renderMenu()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'argus-inbox-room': ArgusInboxRoom;
  }
}
